import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Checkbox, ListItemIcon, ListItemText, Menu, MenuItem, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import { IUser } from '../../../types/users';

interface IProps {
  anchorEl: HTMLElement | null;
  menuPosition?: { top: number; left: number } | null;
  selectedItem: IUser | null;
  isSkipCrmAllowedInOrg?: boolean;
  checkCanEditUser: (user: IUser) => boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSkipCrm: () => void;
}

export const UsersContextMenu = ({
  anchorEl,
  menuPosition,
  selectedItem,
  isSkipCrmAllowedInOrg,
  checkCanEditUser,
  onClose,
  onEdit,
  onDelete,
  onSkipCrm,
}: IProps) => {
  const theme = useTheme();

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onClose();
    },
    [onClose],
  );

  const handleDelete = useCallback(() => {
    onDelete();
    onClose();
  }, [onClose, onDelete]);

  const handleSkipCrm = useCallback(() => {
    onSkipCrm();
    onClose();
  }, [onClose, onSkipCrm]);

  const canEdit = useMemo(() => !!selectedItem && checkCanEditUser(selectedItem), [selectedItem, checkCanEditUser]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={!!menuPosition}
      onClose={onClose}
      onContextMenu={handleContextMenu}
      anchorReference="anchorPosition"
      anchorPosition={menuPosition || undefined}
      autoFocus={false}
      disableAutoFocus={true}
      disableEnforceFocus={true}
      disableRestoreFocus={true}
    >
      <MenuItem onClick={onEdit}>
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit</ListItemText>
      </MenuItem>
      {canEdit && (
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      )}
      {isSkipCrmAllowedInOrg && canEdit && (
        <MenuItem
          onClick={handleSkipCrm}
          sx={{ borderTop: `1px solid ${theme.palette.divider}`, paddingLeft: 0.75, paddingTop: 1 }}
        >
          <ListItemIcon>
            <Checkbox size="small" checked={selectedItem?.crm_skip_allowed || false} disabled={!canEdit} />
          </ListItemIcon>
          <ListItemText>Allow Skip CRM</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
};
